import React, { createContext, useContext, useEffect, useState } from "react";
import {
  UseSearchFilters,
  lastContactFilterSchema,
  useSearchFilters
} from "@bonliva-crm/ui/search-filters";
import { useInitialValues } from "./use-initial-values";
import { mapFiltersToConsultantQueryInput } from "../utils/mapping";
import {
  ConsultantsQueryInput,
  useGetAllConsultantTagsLazyQuery
} from "@bonliva-crm/graphql";
import { useUserProfile } from "@hooks";
import { isEqual, omit } from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ColumnNameSelectorHook,
  useColumnNameSelector,
  defaultColumns,
  qualityColumns
} from "../components/consultant-table/use-column-name-selector";
import { parseSearchProfileFilters } from "./utils";
import { ConsultantFilterState } from "../utils/consultant-filter-state";

type ConsultantSearchFiltersReturnType = {
  queryInput: ConsultantsQueryInput;
  filterManager: UseSearchFilters<ConsultantFilterState>;
  /**
   * Form has non-empty value in any field
   */
  hasValues: boolean;
  columnNameSelectorHook: ColumnNameSelectorHook;
};

const ConsultantSearchFiltersContext = createContext<ConsultantSearchFiltersReturnType>(
  null!
);

export const useConsultantSearchFilters = () => {
  const context = useContext(ConsultantSearchFiltersContext);

  if (context == null) {
    throw new Error(
      "useConsultantSearchFilters must be used within ConsultantSearchFiltersProvider"
    );
  }

  return context;
};

export const ConsultantSearchFiltersProvider = ({ children }) => {
  const { initialValues, emptyValues } = useInitialValues();
  const user = useUserProfile();
  const columnNameSelectorHook = useColumnNameSelector({
    name: "consultantSearchColumns",
    localeKey: "consultantTableColumnType",
    defaultColumns,
    altPresetColumns: qualityColumns
  });

  const [queryInput, setQueryInput] = useState<ConsultantsQueryInput>(
    mapFiltersToConsultantQueryInput(initialValues, user.myProfile._id)
  );

  const [getTags, { loading: loadingTags, data: dataTags }] =
    useGetAllConsultantTagsLazyQuery({
      fetchPolicy: "cache-and-network"
    });

  const [hasValues, setHasValues] = useState(false);

  const filterManager = useSearchFilters<ConsultantFilterState>({
    schema: yupResolver(lastContactFilterSchema),
    initialValues: initialValues,
    parseSearchProfileFilters,
    localStorageKey: "consultantSearchFavoriteFields",
    activeFilters: [
      // Ansvarig BA
      "responsiblePerson",

      // Arbetat via konkurrent
      "competitors",

      // Bokningsstatus
      "bookingStatus",

      // Bokstav förnamn
      "alphabetFilter",

      // Bokstav efternamn
      "initialsLastName",

      // Föredraget schema
      "workshifts",

      // GDPR: Marknadsföring
      "gdprMarketing",

      // GDPR: Behovsutskick - E-post
      "gdprJobMailingEmail",

      // GDPR: Behovsutskick - SMS
      "gdprJobMailingSms",

      // GDPR: Integritetspolicy
      "gdprIntegrationPolicy",

      // GDPR: Laglig grund
      "legalBasis",

      // Har email
      "hasEmail",

      // Har körkort
      "hasDrivingLicence",

      // Har telefonnummer
      "hasPhone",

      // HPR-nummer
      "hprNumber",

      // Intresserad av arbete i
      "regions",

      // Jobbat i Norge
      "workedInNorway",

      // Kan arbeta i
      "availableForWorkInCountries",

      // Kom i kontakt via
      "initialContact",

      // Kontaktstatus
      "fitness",

      // Kunder
      "customerIds",

      // Kvalitetssäkringsdokument
      "qualityCheck",

      // Kvalitetssäkring Nationella
      "nationalQualification",

      // Kvalitetssäkring Sverige
      "qualification",

      // Lead-status
      "leadStatus",

      // Lön/ersättningspråk per timme
      "wagesRange",

      // Nästa kontakt
      "nextContact",

      // Pipeline status
      "pipelineStatus",

      // Postnummer
      "location",

      // Ring- och kontaktlistor
      "callListIds",

      // Samarbetsform
      "hasLegalPersonType",

      // Senaste kontakt
      "lastContact",

      // Sökprofiler
      "searchProfile",

      // Systemkunskaper
      "knownSystems",

      // Taggar
      "tags",

      // Tillgänglighet
      "availability",

      // Yrkestitel
      "profession",

      // Ålder
      "ageRange"
    ],
    onChange: (value) => {
      setQueryInput(mapFiltersToConsultantQueryInput(value, user.myProfile._id));
    },
    overrides: {
      tags: {
        name: "tags",
        getOptions: () => {
          getTags();
        },
        tags: dataTags?.getAllConsultantTags?.tags ?? [],
        loading: loadingTags
      }
    }
  });

  const values = filterManager.formMethods.watch();

  useEffect(() => {
    const omitFields = ["sort", "text", "fullTextSearch"];
    setHasValues(!isEqual(omit(emptyValues, omitFields), omit(values, omitFields)));
  }, [emptyValues, values]);

  useEffect(() => {
    if (values?.text) {
      values?.sort.sortBy !== null &&
        filterManager.formMethods.setValue("sort", { sortBy: null, sortOrder: null });
    } else {
      values?.sort.sortBy === null &&
        filterManager.formMethods.setValue("sort", initialValues.sort);
    }
  }, [values?.text]);

  return (
    <ConsultantSearchFiltersContext.Provider
      value={{
        queryInput,
        filterManager,
        hasValues,
        columnNameSelectorHook
      }}
    >
      {children}
    </ConsultantSearchFiltersContext.Provider>
  );
};
